/**
 * The browse list on /events (the app agent's YOUR-EVENT-PAGE-REVIEWED-AND-THE-LIST-GAP.md §2).
 *
 * The hero shows what `/catalog?featured=1` returns — two events. Everything else the catalogue publishes was
 * unreachable on kgob.com, which also orphaned the per-event pages: nothing linked to them.
 *
 * Two rules that are ours to enforce, not theirs:
 *  - `visibility` is filtered **here**. Their /catalog deliberately includes `unlisted` so an invitation-only
 *    event still renders for someone holding the link. A list is exactly where that leaks.
 *  - a `cancelled` event stays in the list with its badge. Dropping it silently means someone with it in their
 *    calendar finds nothing and assumes they have the wrong day.
 */
import { actionFor, badgeFor, nextDate, upcomingSessions } from "./availability";
import { gateLink, rsvpTarget } from "./gate";
import { placeOf } from "./hero";
import { fmtWhen } from "./time";

function row(event, now) {
  const next = nextDate(event, now);
  const cancelled = event.status === "cancelled";
  const action = next && !cancelled ? actionFor(next, event, now) : { kind: "none" };
  return {
    slug: event.slug,
    title: event.title,
    tagline: event.tagline || null,
    type: event.type ? { name: event.type.name, hue: event.type.hue } : null,
    href: `/events/${event.slug}`,
    dates: upcomingSessions(event, now).length,
    cancelled,
    next: next
      ? { starts_at: next.starts_at, when: fmtWhen(next), place: placeOf(next), badge: badgeFor(next, event, now) }
      : null,
    rsvp: action.kind === "rsvp"
      ? { href: gateLink(rsvpTarget(event.slug, next.event_session_id)), label: action.label, event_session_id: next.event_session_id }
      : null,
  };
}

function windowDays(catalog, now) {
  const from = catalog && catalog.window && catalog.window.from;
  if (!from) return null;
  const days = Math.floor((now.getTime() - new Date(from).getTime()) / 86400000);
  return days > 0 && days < 400 ? days : null;
}

/** {upcoming, past, count} from a /api/events/catalog payload. Public events only, soonest first. */
export function buildList(catalog, now = new Date()) {
  const rows = ((catalog && catalog.items) || []).filter((e) => e && e.visibility === "public" && e.slug && e.title).map((e) => row(e, now));
  return {
    upcoming: (rows || []).filter((r) => r.next).sort((a, b) => a.next.starts_at.localeCompare(b.next.starts_at)),
    past: (rows || []).filter((r) => !r.next),
    count: rows.length,
    stale: !!(catalog && (catalog.stale || catalog.unreachable)),
    // Their ask (2026-09-15): "we sent events and you kept none" is a defect and must never read as "no events
    // scheduled". sent/kept come from /api/events/catalog; dropped carries the first validation error each.
    defect: !!(catalog && catalog.sent > 0 && catalog.kept === 0),
    dropped: (catalog && catalog.dropped) || null,
    // They echo the window they used ({from, to}), so the past section can be labelled honestly rather than
    // vaguely — "finished in the last 60 days" is a promise we can keep, "events that have finished" is not.
    windowDays: windowDays(catalog, now),
  };
}
